import React, { useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";

import api, { getApiErrorMessage } from "../../api";

const TABS = [
  { id: "customer-summary", label: "Customer Summary" },
  { id: "outstanding", label: "Outstanding" },
  { id: "rating-report", label: "Rating Report" },
  { id: "activity-log", label: "Activity Log" },
  { id: "overdraft", label: "Overdraft" },
  { id: "credit-utilization", label: "Credit Utilization" },
];

function formatMoney(value) {
  return `Rs. ${Number(value || 0).toFixed(2)}`;
}

function formatGrams(value) {
  return `${Number(value || 0).toFixed(3)} g`;
}

function MetalExchangeReports() {
  const today = new Date().toISOString().slice(0, 10);
  const monthStart = `${today.slice(0, 8)}01`;
  const [tab, setTab] = useState("customer-summary");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({
    min_rating: "1",
    max_rating: "5",
    block_status: "ALL",
    customer_id: "",
    view: "MONTH",
    date_from: monthStart,
    date_to: today,
  });
  const [customerSummary, setCustomerSummary] = useState({ data: [] });
  const [outstanding, setOutstanding] = useState({ data: [] });
  const [ratings, setRatings] = useState({ data: [] });
  const [activity, setActivity] = useState({ data: [] });
  const [overdraft, setOverdraft] = useState({ data: [] });
  const [creditUtilization, setCreditUtilization] = useState({ data: [] });

  const loadReports = async () => {
    setLoading(true);
    try {
      const [summaryRes, outstandingRes, ratingRes, activityRes, overdraftRes, creditRes] = await Promise.all([
        api.get(`/metal-exchange/reports/customer-summary?min_rating=${filters.min_rating}&max_rating=${filters.max_rating}&block_status=${filters.block_status}`),
        api.get("/metal-exchange/reports/outstanding"),
        api.get("/metal-exchange/reports/rating-report"),
        api.get(`/metal-exchange/reports/activity-log?view=${filters.view}&date_from=${filters.date_from}&date_to=${filters.date_to}&customer_id=${filters.customer_id || ""}`),
        api.get("/metal-exchange/reports/overdraft"),
        api.get("/metal-exchange/reports/credit-utilization"),
      ]);
      setCustomerSummary(summaryRes.data || { data: [] });
      setOutstanding(outstandingRes.data || { data: [] });
      setRatings(ratingRes.data || { data: [] });
      setActivity(activityRes.data || { data: [] });
      setOverdraft(overdraftRes.data || { data: [] });
      setCreditUtilization(creditRes.data || { data: [] });
      setError("");
    } catch (err) {
      setError(getApiErrorMessage(err, "Failed to load metal exchange reports."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReports();
  }, [filters]);

  const renderTable = (headers, rows, emptyText) => (
    <div className="overflow-auto">
      <table className="min-w-full text-left text-sm">
        <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
          <tr>{headers.map((header) => <th key={header} className="px-4 py-3">{header}</th>)}</tr>
        </thead>
        <tbody>
          {!loading && rows.map((row, index) => <tr key={index} className="border-t border-slate-100">{row}</tr>)}
          {!loading && !rows.length && <tr><td colSpan={headers.length} className="px-4 py-10 text-center text-slate-500">{emptyText}</td></tr>}
          {loading && <tr><td colSpan={headers.length} className="px-4 py-10 text-center text-slate-500">Loading report...</td></tr>}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="space-y-6">
      {error && <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>}

      <section className="rounded-[2rem] border border-slate-200 bg-[linear-gradient(135deg,#0f172a_0%,#1e3a8a_55%,#0369a1_100%)] px-6 py-6 text-white shadow-[0_24px_80px_rgba(15,23,42,0.18)]">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.3em] text-sky-100">Metal Exchange Reports</div>
            <h2 className="mt-2 text-3xl font-bold">Exposure, utilization, overdraft and activity tracking</h2>
            <p className="mt-2 max-w-3xl text-sm text-sky-50">Review customer balances, aging, reliability, utilization, and transaction activity for the redesigned receipt flow.</p>
          </div>
          <button onClick={loadReports} className="inline-flex items-center gap-2 rounded-2xl border border-white/15 bg-white/10 px-4 py-2 text-sm font-semibold text-white transition hover:bg-white/20">
            <RefreshCw size={15} /> Refresh
          </button>
        </div>
      </section>

      <section className="rounded-3xl border border-slate-200 bg-white p-2 shadow-sm">
        <div className="flex flex-wrap gap-2">
          {TABS.map((item) => (
            <button key={item.id} type="button" onClick={() => setTab(item.id)} className={`rounded-2xl px-4 py-2 text-sm font-semibold transition ${tab === item.id ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
              {item.label}
            </button>
          ))}
        </div>
      </section>

      <section className="grid gap-4 md:grid-cols-5">
        <Filter label="Min Rating"><select value={filters.min_rating} onChange={(event) => setFilters((prev) => ({ ...prev, min_rating: event.target.value }))} className="input">{["1", "2", "3", "4", "5"].map((value) => <option key={value} value={value}>{value}</option>)}</select></Filter>
        <Filter label="Max Rating"><select value={filters.max_rating} onChange={(event) => setFilters((prev) => ({ ...prev, max_rating: event.target.value }))} className="input">{["1", "2", "3", "4", "5"].map((value) => <option key={value} value={value}>{value}</option>)}</select></Filter>
        <Filter label="Block Status"><select value={filters.block_status} onChange={(event) => setFilters((prev) => ({ ...prev, block_status: event.target.value }))} className="input"><option value="ALL">ALL</option><option value="ACTIVE">ACTIVE</option><option value="BLOCKED">BLOCKED</option></select></Filter>
        <Filter label="Date From"><input type="date" value={filters.date_from} onChange={(event) => setFilters((prev) => ({ ...prev, date_from: event.target.value }))} className="input" /></Filter>
        <Filter label="Date To"><input type="date" value={filters.date_to} onChange={(event) => setFilters((prev) => ({ ...prev, date_to: event.target.value }))} className="input" /></Filter>
      </section>

      <section className="rounded-3xl border border-slate-200 bg-white shadow-sm">
        {tab === "customer-summary" && renderTable(
          ["Customer", "Balances", "Limits", "Rating"],
          (customerSummary.data || []).map((row) => (
            <>
              <td className="px-4 py-3 font-semibold text-slate-900">{row.customer_name}<div className="text-xs text-slate-500">{row.customer_code}</div></td>
              <td className="px-4 py-3">{formatGrams(row.current_metal_balance_grams)}<div className="text-xs text-slate-500">{formatMoney(row.current_cash_balance_rupees)}</div></td>
              <td className="px-4 py-3">{formatGrams(row.metal_credit_limit_grams)}<div className="text-xs text-slate-500">{formatMoney(row.cash_credit_limit_rupees)}</div></td>
              <td className="px-4 py-3">{row.metal_exchange_rating}/5</td>
            </>
          )),
          "No customer summary rows found."
        )}

        {tab === "outstanding" && renderTable(
          ["Receipt", "Customer", "Balance", "Due", "Status"],
          (outstanding.data || []).map((row) => (
            <>
              <td className="px-4 py-3 font-semibold text-slate-900">{row.tx_no}</td>
              <td className="px-4 py-3">{row.customer}</td>
              <td className="px-4 py-3">{formatGrams(row.balance_g)}<div className="text-xs text-slate-500">{formatMoney(row.balance_rs)}</div></td>
              <td className="px-4 py-3">{row.due_date || "-"}</td>
              <td className="px-4 py-3">{row.status}{row.days_overdue > 0 && <div className="text-xs text-rose-600">{row.days_overdue} day(s) overdue</div>}</td>
            </>
          )),
          "No outstanding records found."
        )}

        {tab === "rating-report" && renderTable(
          ["Customer", "Metal Rating", "Payment Rating", "Performance"],
          (ratings.data || []).map((row) => (
            <>
              <td className="px-4 py-3 font-semibold text-slate-900">{row.name}<div className="text-xs text-slate-500">{row.code}</div></td>
              <td className="px-4 py-3">{row.metal_exchange_rating}/5</td>
              <td className="px-4 py-3">{row.payment_rating}/5</td>
              <td className="px-4 py-3">On Time {row.on_time_count} · Late {row.late_count}<div className="text-xs text-slate-500">{Number(row.total_kg || 0).toFixed(3)} kg</div></td>
            </>
          )),
          "No rated customers found."
        )}

        {tab === "activity-log" && renderTable(
          ["Receipt", "Date", "Customer", "Type", "Totals"],
          (activity.data || []).map((row) => (
            <>
              <td className="px-4 py-3 font-semibold text-slate-900">{row.tx_no}</td>
              <td className="px-4 py-3">{String(row.date || "").slice(0, 10)}</td>
              <td className="px-4 py-3">{row.customer_name}</td>
              <td className="px-4 py-3">{row.type}<div className="text-xs text-slate-500">{row.approval_status}</div></td>
              <td className="px-4 py-3">{formatGrams(row.quantity_g)}<div className="text-xs text-slate-500">{formatMoney(row.amount_rs)}</div></td>
            </>
          )),
          "No activity rows found."
        )}

        {tab === "overdraft" && renderTable(
          ["Customer", "Metal", "Cash", "Limits"],
          (overdraft.data || []).map((row) => (
            <>
              <td className="px-4 py-3 font-semibold text-slate-900">{row.customer_name}<div className="text-xs text-slate-500">{row.customer_code}</div></td>
              <td className="px-4 py-3">{formatGrams(row.metal_balance_g)}</td>
              <td className="px-4 py-3">{formatMoney(row.cash_balance_rs)}</td>
              <td className="px-4 py-3">{formatGrams(row.metal_credit_limit_grams)}<div className="text-xs text-slate-500">{formatMoney(row.cash_credit_limit_rupees)}</div></td>
            </>
          )),
          "No overdraft customers found."
        )}

        {tab === "credit-utilization" && renderTable(
          ["Customer", "Metal Used / Limit", "Cash Used / Limit", "Utilization", "Risk"],
          (creditUtilization.data || []).map((row) => (
            <>
              <td className="px-4 py-3 font-semibold text-slate-900">{row.customer_name}<div className="text-xs text-slate-500">{row.customer_code}</div></td>
              <td className="px-4 py-3">{formatGrams(row.metal_used_g)} / {formatGrams(row.metal_limit_g)}</td>
              <td className="px-4 py-3">{formatMoney(row.cash_used_rs)} / {formatMoney(row.cash_limit_rs)}</td>
              <td className="px-4 py-3">{Number(row.utilization_pct || 0).toFixed(1)}%</td>
              <td className="px-4 py-3"><span className={`rounded-full px-3 py-1 text-xs font-semibold ${row.risk_color === "red" ? "bg-rose-100 text-rose-700" : row.risk_color === "amber" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>{row.risk_color}</span></td>
            </>
          )),
          "No utilization rows found."
        )}
      </section>
    </div>
  );
}

function Filter({ label, children }) {
  return <label className="space-y-1"><div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div>{children}</label>;
}

export default MetalExchangeReports;
